import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookingService, talentService } from '../../api/apiService';
import Select from 'react-select';
import { customStyles } from './../../helpers/StyleUtils';
import OrderTypeFilter from '../OrderTypeFilter';
import { getOrderType } from '../../helpers/orderType';
import confirmIcon from '../../images/confirm.svg';
import declineIcon from '../../images/decline.svg';

const ListViewRequested = ({ events, authToken, initialWeek }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [orderTypeFilter, setOrderTypeFilter] = useState('all');
  const [currentWeek, setCurrentWeek] = useState(initialWeek || getCurrentWeek());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const navigate = useNavigate();
  
  const handleChange = event => {
    setSearchTerm(event.target.value);
  };

  const fetchBooking = async (bookingId) => {
    if (!bookingId) {
      console.error("Invalid booking ID provided:", bookingId);
      return null;
    }
    
    try {
      const response = await bookingService.getBookingById(bookingId);
      
      // Handle different API response formats
      if (response.data) {
        // Case 1: { booking: {...} }
        if (response.data.booking) {
          return response.data.booking;
        }
        // Case 2: Direct booking data in response.data
        else if (response.data.id || response.data.start_date) {
          return response.data;
        }
      }
      
      console.error("Booking not found for ID:", bookingId);
      return null;
    } catch (error) {
      console.error("Error fetching booking:", error);
      return null;
    }
  };

  // Fetch talent based on talentId
  const fetchTalent = async (talentId) => {
    if (!talentId) {
      console.error("Invalid talent ID provided:", talentId);
      return null;
    }
    
    try {
      const response = await talentService.getTalentById(talentId);
      
      // Handle different API response formats
      if (response && response.data) {
        // Case 1: { talent: {...} }
        if (response.data.talent) {
          return response.data.talent;
        }
        // Case 2: Direct talent data in response.data
        else if (response.data.id || response.data.Name || response.data.E_Mail) {
          return response.data;
        }
      }
      
      console.error("Talent not found for ID:", talentId);
      return null;
    } catch (error) {
      console.error("Error fetching talent:", error);
      return null;
    }
  };

  const fetchBookingsByGroup = async (groupId) => {
    try {
      const response = await bookingService.getAllBookings(1, 1000);
      if (response.data && response.data.bookings) {
        return response.data.bookings.filter(booking => booking.group === groupId);
      }
      return [];
    } catch (error) {
      console.error("Error fetching bookings by group:", error);
      return [];
    }
  };
  
  const acceptporposedtalent = async (bookingId, skipConfirmation = false) => {
    if (!bookingId) {
      alert("Error: No booking ID provided");
      return;
    }

    // Skip confirmation if called from group function
    if (!skipConfirmation) {
      const userConfirmed = window.confirm("Are you sure you want to accept?");
      if (!userConfirmed) {
        // console.log("Operation cancelled.");
        return;
      }
    }

    try {
      const booking = await fetchBooking(bookingId);
      if (!booking) {
        alert("Booking not found. Please try again.");
        return;
      }

      // Try different possible talentId field names
      const talentId = booking.talent_id || booking.talentId || booking.talent || null;
      
      if (!talentId) {
        alert("No talent assigned to this booking. Please try again.");
        return;
      }

      const talent = await fetchTalent(talentId);
      if (!talent) {
        console.error("Talent data not found for ID:", talentId);
        // Continue without talent data, just update the booking status
        // console.log("Continuing without talent data...");
      }

      // Determine if it's a dummy booking
      const isDummy = booking.isdummy;

      // Update the booking status
      const newStatus = isDummy ? 'talentOnHold' : 'optioned';
      await bookingService.updateBookingStatus(bookingId, newStatus);

      // Send email notification only if we have talent data
      if (talent) {
        await sendEmailNotification(booking, talent);
      }

      // Only show success alert if not in a group operation
      if (!skipConfirmation) {
        alert("Booking updated successfully.");
      }
    } catch (error) {
      console.error("Error in acceptporposedtalent:", error);
      alert(`An error occurred: ${error.message}. Please try again.`);
    }
  };

  const acceptProposedTalentForGroup = async (groupId) => {
    const userConfirmed = window.confirm("Are you sure you want to accept all bookings in this group?");
    if (!userConfirmed) {
      // console.log("Operation cancelled.");
      return;
    }

    try {
      const bookings = await fetchBookingsByGroup(groupId);
      for (const booking of bookings) {
        const bookingId = booking.bookingId || booking.id;
        if (bookingId) {
          // Pass true to skip the confirmation dialog in the individual function
          await acceptporposedtalent(bookingId, true);
        } else {
          console.error("Missing booking ID for booking in group:", booking);
        }
      }
      window.location.reload();
    } catch (error) {
      console.error("Error in acceptProposedTalentForGroup:", error);
    }
  };

  const declineProposedTalentForGroup = async (groupId) => {
    const userConfirmed = window.confirm("Are you sure you want to decline all bookings in this group?");
    if (!userConfirmed) {
      // console.log("Operation cancelled.");
      return;
    }

    try {
      const bookings = await fetchBookingsByGroup(groupId);
      for (const booking of bookings) {
        const bookingId = booking.bookingId || booking.id;
        if (bookingId) {
          // Pass true to skip the confirmation dialog in the individual function
          await declineproposedtalent(bookingId, true);
        } else {
          console.error("Missing booking ID for booking in group:", booking);
        }
      }
      window.location.reload();
    } catch (error) {
      console.error("Error in declineProposedTalentForGroup:", error);
    }
  };

  const declineproposedtalent = async (bookingId, skipConfirmation = false) => {
    if (!bookingId) {
      alert("Error: No booking ID provided");
      return;
    }
    
    // Skip confirmation if called from group function
    if (!skipConfirmation) {
      const userConfirmed = window.confirm("Are you sure you want to decline?");
      if (!userConfirmed) {
        // console.log("Operation cancelled.");
        return;
      }
    }

    try {
      const booking = await fetchBooking(bookingId);
      if (!booking) {
        alert("Booking not found. Please try again.");
        return;
      }

      // Try different possible talentId field names
      const talentId = booking.talent_id || booking.talentId || booking.talent || null;
      let talent = null;
      
      if (talentId) {
        talent = await fetchTalent(talentId);
        if (!talent) {
          console.error("Talent data not found for ID:", talentId);
          // console.log("Continuing without talent data...");
          // Create a minimal talent object with a name to avoid errors
          talent = { name: "Unknown Talent" };
        }
      }

      // Update existing booking to 'deleted' status
      await bookingService.updateBookingStatus(bookingId, 'deleted');

      // Create a new booking with 'new order' status if it's not a dummy booking
      if (!booking.isdummy) {
        // Create a copy of the booking with new status
        const newBookingData = {
          ...booking,
          status: 'new order',
          talent_id: '', // Clear talent ID
          group: null,
          groupColor: '',
        };
        
        // Remove id as the API will generate a new one
        delete newBookingData.id;
        
        // Create the new booking
        const response = await bookingService.createBooking(newBookingData);
        
        // Send notification to the original processing user
        if (booking.processingBy && talent) {
          await bookingService.sendNotification(bookingId, {
            user_id: booking.processingBy,
            message: `Talent ${talent.name || talent.Name || 'Unknown'} rejected the order for ${booking.client?.label || booking.client}. Please propose another talent`,
            booking_id: response.data.booking.id,
            url: 'admin/allnewbookings'
          });
        }
      }

      // Only show success alert if not in a group operation
      if (!skipConfirmation) {
        alert("Booking declined successfully.");
        window.location.reload();
      }
    } catch (error) {
      console.error("Error in declineproposedtalent:", error);
      alert(`An error occurred: ${error.message}. Please try again.`);
    }
  };

  // Helper function to send email notifications
  const sendEmailNotification = async (booking, talent) => {
    try {
      const formattedDate = new Date(booking.start_date).toLocaleDateString('de-DE');
      const rate = booking.customRate || talent.Tagessatz;

      const emailData = {
        to: `booking@set-cast.com, ${talent.email}`,
        subject: `1. Option - ${booking.client?.label || booking.client} - ${talent.name}`,
        template: 'talent_option_notification',
        templateData: {
          talent_name: talent.name,
          client: booking.client?.label || booking.client,
          position: booking.position?.label || booking.position,
          set: booking.set,
          date: formattedDate,
          time: `${booking.start_time}-${booking.end_time}`,
          dayrate: rate,
          kpi: (() => {
            const kpiValue = booking.kpi;
            if (kpiValue === null || kpiValue === undefined || kpiValue === '') {
              return 'Not specified';
            }
            
            // Handle objects (like location objects that might be in the wrong field)
            if (typeof kpiValue === 'object' && kpiValue !== null) {
              return 'Not specified';
            }
            
            // Convert to number and round to integer
            const numericValue = parseFloat(kpiValue);
            if (!isNaN(numericValue)) {
              return Math.round(numericValue);
            }
            
            // If it's not a valid number, return 'Not specified'
            return 'Not specified';
          })(),
          location: `${booking.studio} - ${booking.location}`,
          contact_person: booking.proposedBy
        }
      };
      
      await bookingService.sendEmail(emailData);
      return true;
    } catch (error) {
      console.error("Error sending email notification:", error);
      return false;
    }
  };

  function getWeekNumber(d) {
    // Copy date so don't modify the original
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    
    // Set to nearest Thursday: current date + 4 - current day number
    // Make Sunday's day number 7
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    
    // Get first day of the year
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    
    // Calculate full weeks to nearest Thursday
    const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    
    return weekNo;
  }

  const createTask = async (booking, talent) => {
    // console.log('create called', authToken);
    
    try {
      if (!authToken) {
        console.error('No auth token available');
        return;
      }
      
      const startDate = new Date(booking.start_date);
      const endDate = new Date(booking.end_date);
      
      const kw = 'KW' + getWeekNumber(startDate) + " ";
      const title = kw + '_' + booking.client?.label + '_' + booking.position?.label + '_' + 
                   startDate.toLocaleDateString() + '-' + endDate.toLocaleDateString();
      
      const taskData = {
        task: {
          title: title,
          description: booking.description || ' ',
          details: booking.description || ' ',
          starts_on: startDate.toISOString().split('T')[0],
          ends_on: endDate.toISOString().split('T')[0],
          rate: talent.Tagessatz,
          duration_in_time_units: booking.range,
          currency: "EUR",
          budget_setting: "set_budget",
          assignee_id: talent.Lano_ID,
          rate_type_id: 1,
          creator_id: 216149,
          state: "freelancer_assigned",
          task_cost_centers_attributes: [
            {
              cost_center_1_id: booking.costcenter1?.id,
              cost_center_2_id: booking.costcenter2?.id
            }
          ]
        }
      };
      
      // Use the bookingService to create a Lano task
      await bookingService.createLanoTask(taskData, authToken);
      // console.log('Task created successfully');
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  // Add helper functions for complex object fields display

  // Helper function to get client display 
  const getClientDisplay = (event) => {
    if (!event) return '';
    
    if (event.client) {
      // Handle client as an object with label property
      if (typeof event.client === 'object' && event.client.label) {
        return event.client.label;
      }
      
      // Handle client as a string
      if (typeof event.client === 'string') {
        return event.client;
      }
    }
    
    return '';
  };

  // Helper function to get position display
  const getPositionDisplay = (event) => {
    if (!event) return '';
    
    if (event.position) {
      // Handle position as an object with label property
      if (typeof event.position === 'object' && event.position.label) {
        return event.position.label;
      }
      
      // Handle position as a string
      if (typeof event.position === 'string') {
        return event.position;
      }
    }
    
    return '';
  };

  // Helper function to get talent display
  const getTalentDisplay = (event) => {
    if (!event) return '';
    
    if (event.talent) {
      // Handle talent as an object with name property
      if (typeof event.talent === 'object' && event.talent.name) {
        return event.talent.name;
      }
      
      // Handle talent as a string
      if (typeof event.talent === 'string') {
        return event.talent;
      }
    }
    
    // Try talent_name field if talent is not available
    if (event.talent_name && typeof event.talent_name === 'string') {
      return event.talent_name;
    }
    
    return '';
  };

  // Helper function to get location display
  const getLocationDisplay = (event) => {
    if (!event) return '';
    
    // Handle location output formatting
    let locationText = '';
    
    // Check if location is an object
    if (event.location && typeof event.location === 'object') {
      // Access name property or short if name doesn't exist
      locationText = event.location.name || event.location.short || '';
    } else if (typeof event.location === 'string') {
      locationText = event.location;
    }
    
    return locationText;
  };

  // Helper function to get studio display
  const getStudioDisplay = (event) => {
    if (!event) return '';
    
    // Handle studio output formatting
    if (typeof event.studio === 'string') {
      return event.studio;
    } else if (event.studio && typeof event.studio === 'object') {
      return event.studio.name || '';
    }
    
    return '';
  };

  // Helper function for option field
  const getOptionDisplay = (event) => {
    if (!event) return '';
    
    if (event.option) {
      // Handle option as an object with label or value property
      if (typeof event.option === 'object') {
        return event.option.label || event.option.value || '';
      }
      
      // Handle option as a string
      if (typeof event.option === 'string') {
        return event.option;
      }
    }
    
    return '';
  };

  // Helper function for SonstigeInfo field
  const getSonstigeInfoDisplay = (event) => {
    if (!event) return '';
    
    // Check different possible property names for additional info
    if (event.SonstigeInfo) {
      if (typeof event.SonstigeInfo === 'object') {
        return event.SonstigeInfo.text || event.SonstigeInfo.value || '';
      }
      return event.SonstigeInfo;
    }
    
    // Check alternative property names
    if (event.sonstige_info) {
      if (typeof event.sonstige_info === 'object') {
        return event.sonstige_info.text || event.sonstige_info.value || '';
      }
      return event.sonstige_info;
    }
    
    // Check for additional_info property
    if (event.additional_info) {
      if (typeof event.additional_info === 'object') {
        return event.additional_info.text || event.additional_info.value || '';
      }
      return event.additional_info;
    }
    
    return '';
  };

  const filteredEvents = events.filter(
    event => {
      // Week filtering
      const eventDate = new Date(event.start);
      const { weekStart, weekEnd } = getWeekDates(currentWeek, currentYear);
      const isInCurrentWeek = eventDate >= weekStart && eventDate <= weekEnd;

      const matchesSearch = !searchTerm || (
        event.boktitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.set?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.talent?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.company?.toLowerCase().includes(searchTerm.toLowerCase())
      );

      // User filtering - check created_by, proposed_by, and processing_by fields
      const matchesUser = !selectedUser || (
        event.created_by === selectedUser.value ||
        event.proposed_by === selectedUser.value ||
        event.proposedBy === selectedUser.value ||
        event.processing_by === selectedUser.value
      );

      const matchesOrderType = orderTypeFilter === 'all' || getOrderType(event) === orderTypeFilter;

      return isInCurrentWeek && matchesSearch && matchesUser && matchesOrderType;
    }
  );

  // Create user options from events
  const getUsersFromEvents = () => {
    const users = new Set();
    events.forEach(event => {
      if (event.created_by) users.add(event.created_by);
      if (event.proposed_by) users.add(event.proposed_by);
      if (event.proposedBy) users.add(event.proposedBy);
      if (event.processing_by) users.add(event.processing_by);
    });
    return Array.from(users).filter(user => user && user.trim() !== '')
      .map(user => ({ value: user, label: user }));
  };

  const userOptions = getUsersFromEvents();

  // Helper function to get current week number (ISO 8601)
  function getCurrentWeek() {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Set both dates to Thursday of their respective weeks
    // This ensures we get the correct ISO week number
    start.setDate(start.getDate() + (4 - (start.getDay() || 7)));
    today.setDate(today.getDate() + (4 - (today.getDay() || 7)));
    
    const weekNumber = Math.ceil((((today - start) / 86400000) + 1) / 7);
    return weekNumber;
  }

  // Helper function to get ISO week number
  function getISOWeek(date) {
    const target = new Date(date.valueOf());
    const dayNr = (date.getDay() + 6) % 7;
    target.setDate(target.getDate() - dayNr + 3);
    const firstThursday = target.valueOf();
    target.setMonth(0, 1);
    if (target.getDay() !== 4) {
      target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
    }
    return 1 + Math.ceil((firstThursday - target) / 604800000);
  }

  // Helper function to get dates for a specific week and year
  function getWeekDates(weekNumber, year) {
    // Get January 4th of the year (this is always in week 1 according to ISO 8601)
    const jan4 = new Date(year, 0, 4);
    
    // Find the Monday of week 1
    const mondayOfWeek1 = new Date(jan4);
    mondayOfWeek1.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7));
    
    // Calculate the Monday of the specified week (start of day)
    const weekStart = new Date(mondayOfWeek1);
    weekStart.setDate(mondayOfWeek1.getDate() + (weekNumber - 1) * 7);
    weekStart.setHours(0, 0, 0, 0);

    // Calculate the Sunday of the specified week (END of day, so Saturday AND Sunday
    // bookings are inside the range — booking dates carry a time component)
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    return { weekStart, weekEnd, year: weekStart.getFullYear() };
  }

  // Navigation functions
  const goToPreviousWeek = () => {
    if (currentWeek === 1) {
      // Go to last week of previous year
      setCurrentYear(currentYear - 1);
      setCurrentWeek(52); // Most years have 52 weeks
    } else {
      setCurrentWeek(currentWeek - 1);
    }
  };

  const goToNextWeek = () => {
    const { weekStart } = getWeekDates(currentWeek + 1, currentYear);
    const weekYear = weekStart.getFullYear();
    
    if (weekYear > currentYear) {
      // Moving to next year
      setCurrentYear(weekYear);
      setCurrentWeek(1);
    } else if (currentWeek >= 52) {
      // Check if year has 53 weeks
      const dec31 = new Date(currentYear, 11, 31);
      const lastWeek = getISOWeek(dec31);
      if (currentWeek < lastWeek) {
        setCurrentWeek(currentWeek + 1);
      } else {
        // Go to first week of next year
        setCurrentYear(currentYear + 1);
        setCurrentWeek(1);
      }
    } else {
      setCurrentWeek(currentWeek + 1);
    }
  };

  const goToCurrentWeek = () => {
    const now = new Date();
    setCurrentWeek(getISOWeek(now));
    setCurrentYear(now.getFullYear());
  };

  return (
    <div>
      <div></div>
      {/* Week Navigation */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '10px', 
        marginBottom: '20px',
        padding: '10px',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px'
      }}>
        <button 
          className="button"
          onClick={goToPreviousWeek}
        >
          Previous Week
        </button>
        <div style={{ 
          padding: '13px 16px', 
          backgroundColor: '#fff',
          border: '1px solid #000',
          fontWeight: '600'
        }}>
          Week {currentWeek} of {currentYear}
        </div>
        <button 
          className="button"
          onClick={goToNextWeek}
        >
          Next Week
        </button>
        <button 
          className="button"
          onClick={goToCurrentWeek}
        >
          Current Week
        </button>
      </div>

      <div style={{ marginTop: '40px', marginBottom: '60px' }}>
        <input
          type="text"
          placeholder="Search"
          value={searchTerm}
          onChange={handleChange}
          className='inputtext search'
        />
        
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
          <Select
            value={selectedUser}
            onChange={setSelectedUser}
            options={userOptions}
            isClearable
            placeholder="Filter by User"
            styles={customStyles}
          />
          <OrderTypeFilter
            value={orderTypeFilter}
            onChange={setOrderTypeFilter}
            styles={customStyles}
          />
        </div>
      </div>

      <div className='content-wrapper content-wrapper-talent-list'>
        <div className='list-tasks-header'>
          <div style={{ flexBasis: '200px' }}>start/end</div>
          <div style={{ flexBasis: '150px' }}>set</div>
          <div style={{ flexBasis: '50px' }}>kpi</div>
          <div style={{ flexBasis: '150px' }}>pos.</div>
          <div style={{ flexBasis: '100px' }}>option</div>
          <div style={{ flexBasis: '150px' }}>talent</div>
          <div style={{ flexBasis: '100px' }}>status</div>
          <div style={{ flexBasis: '300px' }}>client</div>
          <div style={{ flexBasis: '80px' }}></div>
        </div>
      </div>

      {filteredEvents.map((event, index) => {
        const shouldDisplayIcon = true;
        const bookingId = event.bookingId || event.id; // Use bookingId if available, otherwise fall back to id

        async function handleAccept() {
          if (event.group) {
            await acceptProposedTalentForGroup(event.group);
          } else {
            acceptporposedtalent(bookingId);
          }
        }

        async function handleDecline() {
          if (event.group) {
            await declineProposedTalentForGroup(event.group);
          } else {
            declineproposedtalent(bookingId);
          }
        }

        return (
          <div className='list-tasks-item' key={bookingId || index}>
            <div style={{ flexBasis: '200px' }}>
              {new Date(event.start).toLocaleDateString()} - {new Date(event.end).toLocaleDateString()}
            </div>
            <div style={{ flexBasis: '150px' }}>{event.set}</div>
            <div style={{ flexBasis: '50px' }}>{event.kpi}</div>
            <div style={{ flexBasis: '150px' }}>{getPositionDisplay(event)}</div>
            <div style={{ flexBasis: '100px' }}>{getOptionDisplay(event)}</div>
            <div style={{ flexBasis: '150px' }}>{getTalentDisplay(event)}</div>
            <div style={{ flexBasis: '100px' }}>{event.status}</div>
            <div style={{ flexBasis: '300px' }}>{getClientDisplay(event)}</div>
            {shouldDisplayIcon && (
              <div style={{ flexBasis: '80px' }}>
                <a className='edit-button' onClick={handleAccept} >
                  <img src={confirmIcon} alt="Accept" className="edit-container" />
                </a>
                <a className='edit-button' onClick={handleDecline}>
                  <img src={declineIcon} alt="Decline" className="edit-container" />
                </a>
              </div>
            )}
          </div>
        );
      })}
      <div style={{ marginTop: '80px' }}>
        <p><i>Note: Accepting or declining the option is on behalf of the talent.</i></p>
      </div>
    </div>
  );
};

export default ListViewRequested;
